// The Academy's content as the screens read it, in two tiers. The outline (levels, modules, every
// lesson's steps with their ids, the drill items, the templates) is a small generated chunk the
// list screens, the model and the sync need; a lesson's full content (check texts, what to look
// for, sources, the Markdown bodies) is fetched when that lesson, or a sitting over it, opens.
// Flag-off devices download neither. `curriculumBundle.ts` knows where the full content comes
// from; `generated/outline.ts` is derived from it and kept in step by a test.
import type {
  Archetype,
  ArchetypePattern,
  LearnManifest,
  LearnManifestDrillItem,
  TasteFormat,
} from "@proef/domain";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { Locale } from "../i18n";

export interface Localised {
  readonly nl: string;
  readonly en: string;
}

/** Lesson bodies exist in nl and en; the other five interface locales read nl until content exists. */
export type ContentLocale = keyof Localised;

export function contentLocale(locale: Locale): ContentLocale {
  return locale === "en" ? "en" : "nl";
}

export function pick(text: Localised, locale: Locale): string {
  return text[contentLocale(locale)];
}

export type { Archetype, ArchetypePattern };

export interface CheckContent {
  readonly id: string;
  readonly prompt: Localised;
  /** 3 or 4 options in authored order; the screen may shuffle for display, never the recorded index. */
  readonly options: readonly Localised[];
  readonly correct: number;
  readonly explanation: Localised;
}

export interface AromaItem {
  readonly id: string;
  readonly kind: "aroma";
  /** The household reference, never a kit vial. */
  readonly reference: Localised;
  readonly description: Localised;
  readonly differs: Localised;
  /** Two or three other descriptor ids: the options besides the answer, authored so the confusion is chosen. */
  readonly distractors: readonly string[];
}

export interface MarkerCue {
  /** The ladder step that is the right call for this sensation. */
  readonly target: string;
  /** The sensation, described in our words. */
  readonly sensation: Localised;
  /** Why that step: shown at the reveal. */
  readonly why: Localised;
}

export interface MarkerItem {
  readonly id: string;
  readonly kind: "marker";
  readonly cues: readonly MarkerCue[];
}

export type DrillContent = AromaItem | MarkerItem;

export interface ReadStepContent {
  readonly id: number;
  readonly kind: "read";
  /** The h2 slug of the body this step shows. */
  readonly section: string;
}
export interface CheckStepContent {
  readonly id: number;
  readonly kind: "check";
  readonly checks: readonly CheckContent[];
}
export interface TasteStepContent {
  readonly id: number;
  readonly kind: "taste";
  readonly format: TasteFormat;
  readonly glass: string;
  readonly lookFor: Localised;
  readonly archetypes?: readonly Archetype[];
  /** "Taste this against that": a horizontal step of exactly two archetypes. */
  readonly pair?: boolean;
}
export interface DrillStepContent {
  readonly id: number;
  readonly kind: "drill";
  readonly items: readonly DrillContent[];
}
export type StepContent = ReadStepContent | CheckStepContent | TasteStepContent | DrillStepContent;

/** A check as the outline knows it: its id, nothing to read. */
export interface CheckOutline {
  readonly id: string;
}
export interface CheckStepOutline {
  readonly id: number;
  readonly kind: "check";
  readonly checks: readonly CheckOutline[];
}
/** A taste step without its "look for" text, which only the open lesson shows. */
export type TasteStepOutline = Omit<TasteStepContent, "lookFor">;
/** A step as the outline knows it: full content steps are assignable to it. */
export type StepOutline = ReadStepContent | CheckStepOutline | TasteStepOutline | DrillStepContent;

export interface SourceContent {
  readonly title: string;
  readonly url: string;
  /** The day the source was read, `YYYY-MM-DD`. */
  readonly accessed: string;
}

/** What every learn screen may know of a lesson without opening it. */
export interface LessonOutline {
  readonly id: string;
  readonly level: number;
  readonly module: string;
  readonly title: Localised;
  readonly steps: readonly StepOutline[];
}

export interface LessonContent extends LessonOutline {
  readonly steps: readonly StepContent[];
  readonly sources: readonly SourceContent[];
  /**
   * The Markdown bodies, `nl.md` and `en.md`, when they travel with the lesson (fixtures). The
   * package's lessons leave them out and load them on open, through lessonBody().
   */
  readonly body?: Localised;
}

export interface TemplateContent {
  readonly id: string;
  readonly level: number;
  readonly subject: { readonly kind: "grape" | "country" | "region"; readonly id: string };
  readonly format: Exclude<TasteFormat, "single">;
  readonly title: Localised;
  readonly archetypes: readonly Archetype[];
  /** `"<lessonId>:<stepId>"` of the taste steps this template can serve. */
  readonly serves: readonly string[];
}

/** An archetype as the outline knows it: what it names, not how the content describes it. */
export type ArchetypeOutline = Pick<Archetype, "index" | "pattern">;

/** A template without its archetypes' descriptions, which only hosting from it reads. */
export interface TemplateOutline extends Omit<TemplateContent, "archetypes"> {
  readonly archetypes: readonly ArchetypeOutline[];
}

export interface ModuleInfo {
  readonly id: string;
  readonly title: Localised;
  readonly lessons: readonly string[];
}

export interface LevelInfo {
  readonly id: number;
  readonly title: Localised;
  readonly modules: readonly ModuleInfo[];
}

/** The outline: what the list screens, the model and the sync read. */
export interface Curriculum {
  /** Changes with content; the mirror records the version it was last refreshed under. */
  readonly version: string;
  readonly levels: readonly LevelInfo[];
  readonly lessons: Readonly<Record<string, LessonOutline>>;
  readonly templates: Readonly<Record<string, TemplateOutline>>;
}

/** Every lesson in full: what the outline is derived from, and what fixtures hand the screens. */
export interface FullCurriculum extends Curriculum {
  readonly lessons: Readonly<Record<string, LessonContent>>;
  readonly templates: Readonly<Record<string, TemplateContent>>;
}

/** The outline with some lessons in full: what a sitting over a level reads. */
export interface CurriculumWithLessons extends Curriculum {
  readonly lessons: Readonly<Record<string, LessonContent>>;
}

export const EMPTY_CURRICULUM: Curriculum = {
  version: "0",
  levels: [],
  lessons: {},
  templates: {},
};

/** Every lesson in curriculum order: levels ascending, then module order, then lesson order. */
export function orderedLessons<L extends LessonOutline>(curriculum: {
  readonly levels: readonly LevelInfo[];
  readonly lessons: Readonly<Record<string, L>>;
}): L[] {
  const out: L[] = [];
  for (const level of curriculum.levels.toSorted((a, b) => a.id - b.id)) {
    for (const module of level.modules) {
      for (const id of module.lessons) {
        const lesson = curriculum.lessons[id];
        if (lesson !== undefined) out.push(lesson);
      }
    }
  }
  return out;
}

export function moduleOf(curriculum: Curriculum, lessonId: string): ModuleInfo | undefined {
  for (const level of curriculum.levels) {
    const found = level.modules.find((module) => module.lessons.includes(lessonId));
    if (found !== undefined) return found;
  }
  return undefined;
}

/** A module's title in the learner's locale, or its id when the curriculum does not know it. */
export function moduleTitle(
  curriculum: Curriculum,
  levelId: number,
  moduleId: string,
  locale: Locale,
): string {
  const module = curriculum.levels
    .find((level) => level.id === levelId)
    ?.modules.find((candidate) => candidate.id === moduleId);
  return module === undefined ? moduleId : pick(module.title, locale);
}

export function levelOf(curriculum: Curriculum, levelId: number): LevelInfo | undefined {
  return curriculum.levels.find((level) => level.id === levelId);
}

export function checksOfStep(step: StepContent): readonly CheckContent[];
export function checksOfStep(step: StepOutline): readonly CheckOutline[];
export function checksOfStep(step: StepOutline): readonly CheckOutline[] {
  return step.kind === "check" ? step.checks : [];
}

/** Every check of a lesson, in order. */
export function checksOfLesson(lesson: LessonContent): CheckContent[];
export function checksOfLesson(lesson: LessonOutline): CheckOutline[];
export function checksOfLesson(lesson: LessonOutline): CheckOutline[] {
  return lesson.steps.flatMap((step) => [...checksOfStep(step)]);
}

/** Every check of a level, in curriculum order: what a prove-it sitting asks. */
export function checksOfLevel(curriculum: CurriculumWithLessons, levelId: number): CheckContent[];
export function checksOfLevel(curriculum: Curriculum, levelId: number): CheckOutline[];
export function checksOfLevel(curriculum: Curriculum, levelId: number): CheckOutline[] {
  return orderedLessons(curriculum)
    .filter((lesson) => lesson.level === levelId)
    .flatMap((lesson) => checksOfLesson(lesson));
}

/** The ids of a level's lessons, in curriculum order: what a sitting over the level must load. */
export function lessonIdsOfLevel(curriculum: Curriculum, levelId: number): string[] {
  return orderedLessons(curriculum)
    .filter((lesson) => lesson.level === levelId)
    .map((lesson) => lesson.id);
}

export function drillItemsOfLesson(lesson: LessonOutline): DrillContent[] {
  return lesson.steps.flatMap((step) => (step.kind === "drill" ? [...step.items] : []));
}

/** Where a drill item lives, for the reveal and the "from lesson" prompt. */
export function findDrillItem(
  curriculum: Curriculum,
  itemId: string,
): { item: DrillContent; lesson: LessonOutline; stepId: number } | undefined {
  for (const lesson of orderedLessons(curriculum)) {
    for (const step of lesson.steps) {
      if (step.kind !== "drill") continue;
      const item = step.items.find((candidate) => candidate.id === itemId);
      if (item !== undefined) return { item, lesson, stepId: step.id };
    }
  }
  return undefined;
}

/** The manifest the pure rules read: ids, step kinds, checks and drill items per lesson, in order. */
export function manifestOf(curriculum: Curriculum): LearnManifest {
  const lessons: Record<string, LearnManifest["lessons"][string]> = {};
  const drillItems: LearnManifestDrillItem[] = [];
  let order = 0;
  for (const lesson of orderedLessons(curriculum)) {
    lessons[lesson.id] = {
      level: lesson.level,
      module: lesson.module,
      steps: lesson.steps.map((step) => ({ id: step.id, kind: step.kind })),
      checks: checksOfLesson(lesson).map((check) => check.id),
      drillItems: drillItemsOfLesson(lesson).map((item) => item.id),
    };
    for (const step of lesson.steps) {
      if (step.kind !== "drill") continue;
      for (const item of step.items) {
        drillItems.push({
          id: item.id,
          kind: item.kind,
          lessonId: lesson.id,
          stepId: step.id,
          order,
        });
        order += 1;
      }
    }
  }
  return {
    levels: curriculum.levels
      .toSorted((a, b) => a.id - b.id)
      .map((level) => ({
        id: level.id,
        modules: level.modules.map((module) => ({ id: module.id, lessons: [...module.lessons] })),
      })),
    lessons,
    drillItems,
  };
}

// ---- loading ------------------------------------------------------------

let current: Curriculum | undefined;
let loading: Promise<Curriculum> | undefined;
/** A load that finishes after a test swapped the content must not undo the swap. */
let epoch = 0;
const listeners = new Set<() => void>();

function publish(curriculum: Curriculum): Curriculum {
  current = curriculum;
  listeners.forEach((listener) => {
    listener();
  });
  return curriculum;
}

/**
 * The version of the content this build carries, known without loading it: the start-up records
 * it in the mirror, and a boot must not fetch 53 lessons to learn one string.
 */
export async function curriculumVersion(): Promise<string> {
  if (import.meta.env["VITE_E2E_CURRICULUM"] === "1") {
    return (await import("../../../test/support/fixtures/curriculum")).curriculum.version;
  }
  return __CURRICULUM_VERSION__;
}

const bodies = new Map<string, Promise<Localised>>();

/** A lesson's bodies: its own when it carries them, else fetched once when the lesson opens. */
export function lessonBody(lesson: LessonContent): Promise<Localised> {
  if (lesson.body !== undefined) return Promise.resolve(lesson.body);
  let pending = bodies.get(lesson.id);
  if (pending === undefined) {
    pending = (async () => {
      try {
        return await (await import("./curriculumBundle")).loadLessonBody(lesson.id);
      } catch (error) {
        // A failed fetch (offline, not cached yet) is not remembered, so reopening retries.
        bodies.delete(lesson.id);
        throw error;
      }
    })();
    bodies.set(lesson.id, pending);
  }
  return pending;
}

async function loadOutline(): Promise<Curriculum> {
  if (import.meta.env["VITE_E2E_CURRICULUM"] === "1") {
    const { curriculum } = await import("../../../test/support/fixtures/curriculum");
    seedContents(curriculum);
    return curriculum;
  }
  return (await import("./generated/outline")).outline;
}

/** The outline, loaded once from its small chunk; later calls share the promise. */
export function loadCurriculum(): Promise<Curriculum> {
  const started = epoch;
  loading ??= loadOutline().then((loaded) => (started === epoch ? publish(loaded) : loaded));
  return loading;
}

// ---- a lesson's full content ---------------------------------------------

const contents = new Map<string, LessonContent>();
const fetching = new Map<string, Promise<LessonContent>>();
const contentListeners = new Set<() => void>();
let contentVersion = 0;

function isLessonContent(lesson: LessonOutline): lesson is LessonContent {
  return "sources" in lesson;
}

const templateContents = new Map<string, TemplateContent>();

function isTemplateContent(template: TemplateOutline): template is TemplateContent {
  return template.archetypes.every((archetype) => "nl" in archetype);
}

/** Fixtures carry every lesson and template whole: those need no fetch. */
function seedContents(curriculum: Curriculum): void {
  for (const lesson of Object.values(curriculum.lessons)) {
    if (isLessonContent(lesson)) contents.set(lesson.id, lesson);
  }
  for (const template of Object.values(curriculum.templates)) {
    if (isTemplateContent(template)) templateContents.set(template.id, template);
  }
}

/** A template already in memory (opened before, or a fixture's), without waiting. */
export function cachedTemplateContent(templateId: string): TemplateContent | undefined {
  return templateContents.get(templateId);
}

/** A template in full (its archetypes' descriptions), for hosting a flight from it. */
export async function loadTemplateContent(templateId: string): Promise<TemplateContent> {
  const known = templateContents.get(templateId);
  if (known !== undefined) return known;
  const template = await (await import("./curriculumBundle")).loadTemplate(templateId);
  templateContents.set(templateId, template);
  return template;
}

/** One lesson's full content, fetched once; a failed fetch is forgotten so reopening retries. */
export function loadLessonContent(lessonId: string): Promise<LessonContent> {
  const known = contents.get(lessonId);
  if (known !== undefined) return Promise.resolve(known);
  let pending = fetching.get(lessonId);
  if (pending === undefined) {
    pending = (async () => {
      try {
        const lesson = await (await import("./curriculumBundle")).loadLesson(lessonId);
        contents.set(lessonId, lesson);
        contentVersion += 1;
        contentListeners.forEach((listener) => {
          listener();
        });
        return lesson;
      } finally {
        fetching.delete(lessonId);
      }
    })();
    fetching.set(lessonId, pending);
  }
  return pending;
}

export type LessonContents =
  | { readonly state: "loading" }
  | { readonly state: "failed" }
  | {
      readonly state: "ready";
      readonly lessons: Readonly<Record<string, LessonContent>>;
      /** Moves with every fetch that lands, so a consumer's memo can follow the cache. */
      readonly version: number;
    };

/**
 * The full content of `lessonIds`, fetched as needed: "loading" until every one is in (a screen
 * shows its skeleton), "failed" when one cannot be had (offline and never opened before).
 */
export function useLessonContents(lessonIds: readonly string[]): LessonContents {
  const key = lessonIds.join("\n");
  const version = useSyncExternalStore(
    (listener) => {
      contentListeners.add(listener);
      return () => contentListeners.delete(listener);
    },
    () => contentVersion,
    () => contentVersion,
  );
  const [failed, setFailed] = useState<string>();
  useEffect(() => {
    let live = true;
    const missing = key === "" ? [] : key.split("\n").filter((id) => !contents.has(id));
    Promise.all(missing.map(loadLessonContent)).catch(() => {
      if (live) setFailed(key);
    });
    return () => {
      live = false;
    };
  }, [key]);
  // `version` moves whenever a fetch lands, so the memo re-reads the cache exactly then.
  return useMemo((): LessonContents => {
    const lessons: Record<string, LessonContent> = {};
    for (const id of key === "" ? [] : key.split("\n")) {
      const lesson = contents.get(id);
      if (lesson === undefined) return failed === key ? { state: "failed" } : { state: "loading" };
      lessons[id] = lesson;
    }
    return { state: "ready", lessons, version } as const;
  }, [version, key, failed]);
}

export function getCurriculum(): Curriculum | undefined {
  return current;
}

/** Loads the outline again after a failed attempt; a finished or running load is left alone. */
export function retryCurriculum(): Promise<Curriculum> {
  if (current === undefined) loading = undefined;
  return loadCurriculum();
}

export type CurriculumLoad =
  | { readonly state: "loading" }
  | { readonly state: "failed"; readonly retry: () => void }
  | { readonly state: "ready"; readonly curriculum: Curriculum };

/**
 * The outline, with a failed load told apart from a slow one: a screen that must not wait forever
 * (the shopping list opens offline with its chunk missing) shows a calm card and a retry instead of
 * a skeleton. Reads the same shared load as {@link useCurriculum}.
 */
export function useCurriculumLoad(): CurriculumLoad {
  const curriculum = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getCurriculum,
    getCurriculum,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (curriculum !== undefined) return undefined;
    let live = true;
    loadCurriculum().catch(() => {
      if (live) setFailed(true);
    });
    return () => {
      live = false;
    };
  }, [curriculum, failed]);
  if (curriculum !== undefined) return { state: "ready", curriculum };
  if (!failed) return { state: "loading" };
  return {
    state: "failed",
    retry: () => {
      setFailed(false);
      void retryCurriculum().catch(() => {
        setFailed(true);
      });
    },
  };
}

/** The content once its chunk has loaded; `undefined` while it loads, so a screen shows its skeleton. */
export function useCurriculum(): Curriculum | undefined {
  const value = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getCurriculum,
    getCurriculum,
  );
  if (value === undefined && loading === undefined) void loadCurriculum();
  return value;
}

/** Test seam: hand the screens a curriculum without loading the chunk. */
export function setCurriculumForTests(curriculum: Curriculum | undefined): void {
  epoch += 1;
  contents.clear();
  templateContents.clear();
  bodies.clear();
  if (curriculum !== undefined) seedContents(curriculum);
  contentVersion += 1;
  contentListeners.forEach((listener) => {
    listener();
  });
  loading = curriculum === undefined ? undefined : Promise.resolve(curriculum);
  current = curriculum;
  listeners.forEach((listener) => {
    listener();
  });
}
